import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  LinearProgress,
  Alert,
  Chip,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  MdSearch,
  MdRefresh,
  MdInfoOutline,
  MdFileDownload,
  MdGridOn,
  MdPrint,
  MdShoppingCart,
  MdHourglassTop,
  MdInventory,
  MdLocalShipping,
  MdCheckCircle,
  MdCancel,
  MdAssignmentReturn,
  MdTimer,
  MdWarningAmber,
} from 'react-icons/md';
import { getData } from '../../Webservice/Webservice';
import {
  FONT,
  inr,
  fmtDate,
  toISO,
  authToken,
  PRESETS,
  presetRange,
  rangeLabel,
  StatCard,
  statGridSx,
  exportButtonSx,
  downloadCsv,
  downloadExcel,
  openPrintView,
} from './reportShared';

const REPORT_ROUTE = 'reports/orders';

const STATUS_STYLES = {
  pending: { label: 'Pending', bg: '#fef3c7', fg: '#92400e' },
  placed: { label: 'Placed', bg: '#e0e7ff', fg: '#3730a3' },
  picking: { label: 'Picking', bg: '#e0f2fe', fg: '#075985' },
  packed: { label: 'Packed', bg: '#cffafe', fg: '#155e75' },
  'ready-for-dispatch': { label: 'Ready for Dispatch', bg: '#ccfbf1', fg: '#115e59' },
  dispatched: { label: 'Dispatched', bg: '#ede9fe', fg: '#5b21b6' },
  'in-transit': { label: 'In Transit', bg: '#f3e8ff', fg: '#6b21a8' },
  'out-for-delivery': { label: 'Out for Delivery', bg: '#fce7f3', fg: '#9d174d' },
  delivered: { label: 'Delivered', bg: '#dcfce7', fg: '#166534' },
  cancelled: { label: 'Cancelled', bg: '#fee2e2', fg: '#991b1b' },
  returned: { label: 'Returned', bg: '#ffedd5', fg: '#9a3412' },
  failed: { label: 'Failed', bg: '#f1f5f9', fg: '#475569' },
};

const PAYMENT_STATUS_STYLES = {
  paid: { bg: '#dcfce7', fg: '#166534' },
  pending: { bg: '#fef3c7', fg: '#92400e' },
  processing: { bg: '#e0f2fe', fg: '#075985' },
  failed: { bg: '#fee2e2', fg: '#991b1b' },
};

const PAYMENT_MODES = ['cod', 'upi', 'card', 'netbanking', 'wallet', 'reward', 'cash', 'others'];

const SORT_OPTIONS = [
  { value: 'date_desc', label: 'Newest first' },
  { value: 'date_asc', label: 'Oldest first' },
  { value: 'amount_desc', label: 'Amount: high to low' },
  { value: 'amount_asc', label: 'Amount: low to high' },
  { value: 'eta_asc', label: 'Expected delivery: soonest' },
  { value: 'eta_desc', label: 'Expected delivery: latest' },
];

const COLUMNS = [
  { key: 'orderNumber', label: 'Order Number' },
  { key: 'orderDate', label: 'Order Date' },
  { key: 'customer', label: 'Customer' },
  { key: 'location', label: 'Location' },
  { key: 'itemsCount', label: 'Items', center: true },
  { key: 'totalAmount', label: 'Amount', numeric: true },
  { key: 'payment', label: 'Payment' },
  { key: 'orderStatus', label: 'Order Status' },
  { key: 'expectedDelivery', label: 'Expected Delivery' },
];

const paymentModeLabel = (m) => (!m || m === '-' ? '-' : m === 'cod' ? 'COD' : m === 'upi' ? 'UPI' : m.charAt(0).toUpperCase() + m.slice(1));

const pill = (bg, fg) => ({ fontWeight: 700, fontFamily: FONT, background: bg, color: fg, height: 24, fontSize: 11 });

const OrderReport = () => {
  const [preset, setPreset] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusGroup, setStatusGroup] = useState('');
  const [status, setStatus] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [paymentMode, setPaymentMode] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  const filterParams = useMemo(() => {
    const p = { sortBy };
    if (from) p.from = from;
    if (to) p.to = to;
    if (search) p.search = search;
    if (status) p.status = status;
    else if (statusGroup) p.statusGroup = statusGroup;
    if (paymentStatus) p.paymentStatus = paymentStatus;
    if (paymentMode) p.paymentMode = paymentMode;
    return p;
  }, [from, to, search, status, statusGroup, paymentStatus, paymentMode, sortBy]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getData(REPORT_ROUTE, { ...filterParams, page: page + 1, limit: rowsPerPage }, authToken(), false);
      if (res?.success) setData(res);
      else setError(res?.message || 'Failed to load report');
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filterParams, page, rowsPerPage]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const applyPreset = (key) => {
    const range = presetRange(key);
    setPreset(key);
    setFrom(range.from);
    setTo(range.to);
    setPage(0);
  };

  const onDateChange = (setter) => (e) => {
    setter(e.target.value);
    setPreset('');
    setPage(0);
  };

  const toggleGroup = (group) => {
    setStatus('');
    setStatusGroup((g) => (g === group ? '' : group));
    setPage(0);
  };

  const resetFilters = () => {
    setPreset('all');
    setFrom('');
    setTo('');
    setSearchInput('');
    setSearch('');
    setStatusGroup('');
    setStatus('');
    setPaymentStatus('');
    setPaymentMode('');
    setSortBy('date_desc');
    setPage(0);
  };

  const exportObjects = (rows) =>
    rows.map((r) => ({
      'Order Number': r.orderNumber,
      'Order Date': fmtDate(r.orderDate),
      Customer: r.customer,
      Phone: r.phone,
      Location: r.location,
      Items: r.itemsCount,
      Amount: r.totalAmount,
      'Payment Method': paymentModeLabel(r.paymentMode),
      'Payment Status': r.paymentStatus,
      'Order Status': STATUS_STYLES[r.orderStatus]?.label || r.orderStatus,
      'Expected Delivery': fmtDate(r.expectedDelivery),
      'Delivered On': fmtDate(r.deliveredAt),
      Delayed: r.isDelayed ? 'Yes' : 'No',
    }));

  const fileStamp = () => `Order_Report_${from || 'all'}_${to || toISO(new Date())}`;

  const runExport = async (kind) => {
    setExporting(true);
    try {
      const res = await getData(REPORT_ROUTE, { ...filterParams, export: 'all' }, authToken(), false);
      if (!res?.success) throw new Error(res?.message || 'Export failed');
      const rows = res.rows || [];
      if (kind === 'csv') downloadCsv(exportObjects(rows), fileStamp());
      else if (kind === 'excel') downloadExcel(exportObjects(rows), fileStamp(), 'Orders');
      else
        openPrintView({
          title: 'Order Report',
          subtitle: `${rangeLabel(from, to)} · ${rows.length} orders`,
          headers: ['Order Number', 'Order Date', 'Customer', 'Location', 'Items', 'Amount', 'Payment', 'Order Status', 'Expected Delivery'],
          cells: rows.map((r) => [
            { value: r.orderNumber },
            { value: fmtDate(r.orderDate) },
            { value: r.customer },
            { value: r.location },
            { value: r.itemsCount, className: 'num' },
            { value: inr(r.totalAmount), className: 'num' },
            { value: `${paymentModeLabel(r.paymentMode)} · ${r.paymentStatus}` },
            { value: STATUS_STYLES[r.orderStatus]?.label || r.orderStatus },
            {
              value: r.orderStatus === 'delivered' ? `Delivered ${fmtDate(r.deliveredAt)}` : `${fmtDate(r.expectedDelivery)}${r.isDelayed ? ' (Delayed)' : ''}`,
              className: r.isDelayed ? 'warn' : '',
            },
          ]),
        });
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const stats = data?.stats || {};
  const total = data?.meta?.total || 0;
  const rows = data?.rows || [];

  const cards = [
    { group: '', label: 'Total Orders', value: (stats.totalOrders || 0).toLocaleString('en-IN'), icon: MdShoppingCart, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', sub: inr(stats.totalValue) },
    { group: 'pending', label: 'Pending', value: stats.pending || 0, icon: MdHourglassTop, gradient: 'linear-gradient(135deg,#f59e0b,#d97706)', sub: 'Pending + placed' },
    { group: 'processing', label: 'Processing', value: stats.processing || 0, icon: MdInventory, gradient: 'linear-gradient(135deg,#0ea5e9,#0284c7)', sub: 'Picking / packed / ready' },
    { group: 'shipped', label: 'Shipped', value: stats.shipped || 0, icon: MdLocalShipping, gradient: 'linear-gradient(135deg,#a855f7,#7e22ce)', sub: 'Dispatched / in transit / OFD' },
    { group: 'delivered', label: 'Delivered', value: stats.delivered || 0, icon: MdCheckCircle, gradient: 'linear-gradient(135deg,#10b981,#059669)', sub: `Avg ${stats.avgDeliveryDays || 0} days to deliver` },
    { group: 'cancelled', label: 'Cancelled', value: stats.cancelled || 0, icon: MdCancel, gradient: 'linear-gradient(135deg,#f43f5e,#dc2626)', sub: `${stats.cancellationRate || 0}% of orders` },
    { group: 'returned', label: 'Returned', value: stats.returned || 0, icon: MdAssignmentReturn, gradient: 'linear-gradient(135deg,#fb923c,#ea580c)', sub: `${stats.returnRate || 0}% of orders` },
    { group: null, label: 'Avg Delivery Time', value: `${stats.avgDeliveryDays || 0} days`, icon: MdTimer, gradient: 'linear-gradient(135deg,#0f172a,#334155)', sub: 'Order placed → delivered' },
  ];

  const activeFilterCount = [from || to, search, status || statusGroup, paymentStatus, paymentMode, sortBy !== 'date_desc'].filter(Boolean).length;

  const renderCell = (c, r) => {
    switch (c.key) {
      case 'orderDate':
        return fmtDate(r.orderDate);
      case 'customer':
        return (
          <Box sx={{ lineHeight: 1.25 }}>
            <Typography sx={{ fontSize: 11.5, fontWeight: 600, fontFamily: FONT }}>{r.customer}</Typography>
            {r.phone && <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>{r.phone}</Typography>}
          </Box>
        );
      case 'totalAmount':
        return inr(r.totalAmount);
      case 'payment': {
        const ps = PAYMENT_STATUS_STYLES[r.paymentStatus] || { bg: '#f1f5f9', fg: '#475569' };
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, fontFamily: FONT, minWidth: 34 }}>{paymentModeLabel(r.paymentMode)}</Typography>
            <Chip size="small" label={r.paymentStatus || '-'} sx={{ ...pill(ps.bg, ps.fg), textTransform: 'capitalize' }} />
          </Box>
        );
      }
      case 'orderStatus': {
        const s = STATUS_STYLES[r.orderStatus] || { label: r.orderStatus, bg: '#f1f5f9', fg: '#475569' };
        return <Chip size="small" label={s.label} sx={pill(s.bg, s.fg)} />;
      }
      case 'expectedDelivery':
        if (r.orderStatus === 'delivered') {
          return (
            <Box sx={{ lineHeight: 1.25 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: '#166534', fontFamily: FONT }}>Delivered {fmtDate(r.deliveredAt)}</Typography>
              <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>ETA was {fmtDate(r.expectedDelivery)}</Typography>
            </Box>
          );
        }
        if (['cancelled', 'returned', 'failed'].includes(r.orderStatus)) return <Typography sx={{ fontSize: 11.5, color: '#94a3b8', fontFamily: FONT }}>—</Typography>;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography sx={{ fontSize: 11.5, fontWeight: 600, fontFamily: FONT, color: r.isDelayed ? '#b91c1c' : '#0f172a' }}>{fmtDate(r.expectedDelivery)}</Typography>
            {r.isDelayed && (
              <Tooltip title={`Past expected delivery · open for ${r.daysOpen} days`} arrow>
                <Chip size="small" icon={<MdWarningAmber />} label="Delayed" sx={{ ...pill('#fee2e2', '#b91c1c'), '& .MuiChip-icon': { color: 'inherit' } }} />
              </Tooltip>
            )}
          </Box>
        );
      default:
        return r[c.key] ?? '-';
    }
  };

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, fontFamily: FONT }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 23.5, fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>Order Report</Typography>
          <Typography sx={{ fontSize: 12.5, color: '#64748b', fontFamily: FONT }}>All orders with their processing status and expected delivery</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" startIcon={<MdInfoOutline />} onClick={() => setNotesOpen(true)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', fontFamily: FONT }}>
            Note
          </Button>
          <Button startIcon={<MdFileDownload />} disabled={exporting || !total} onClick={() => runExport('csv')} sx={exportButtonSx('#16a34a', '#15803d')}>CSV</Button>
          <Button startIcon={<MdGridOn />} disabled={exporting || !total} onClick={() => runExport('excel')} sx={exportButtonSx('#0d9488', '#0f766e')}>Excel</Button>
          <Button startIcon={<MdPrint />} disabled={exporting || !total} onClick={() => runExport('print')} sx={exportButtonSx('#7c3aed', '#6d28d9')}>Print / PDF</Button>
        </Box>
      </Box>

      {/* KPI cards — clicking a status card filters the table to that stage */}
      <Box sx={statGridSx}>
        {cards.map((c) => (
          <StatCard
            key={c.label}
            label={c.label}
            value={c.value}
            icon={c.icon}
            gradient={c.gradient}
            sub={c.sub}
            onClick={c.group === null ? undefined : () => toggleGroup(c.group)}
            active={c.group !== null && !status && statusGroup === c.group && c.group !== ''}
          />
        ))}
      </Box>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', mb: 3, border: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>
            Filters {activeFilterCount > 0 && <Chip size="small" label={`${activeFilterCount} active`} color="primary" sx={{ ml: 1, fontWeight: 700 }} />}
          </Typography>
          <Button size="small" startIcon={<MdRefresh />} onClick={resetFilters} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Reset</Button>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {PRESETS.map((p) => (
            <Chip
              key={p.key}
              label={p.label}
              onClick={() => applyPreset(p.key)}
              sx={{
                fontWeight: 700,
                fontFamily: FONT,
                background: preset === p.key ? 'rgb(255 209 2)' : '#f1f5f9',
                color: '#0f172a',
                border: preset === p.key ? '1px solid #0f172a' : '1px solid transparent',
                '&:hover': { background: preset === p.key ? 'rgb(255 209 2)' : '#e2e8f0' },
              }}
            />
          ))}
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3} lg={1.7}>
            <TextField label="From" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={from} onChange={onDateChange(setFrom)} inputProps={{ max: to || undefined }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={1.7}>
            <TextField label="To" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={to} onChange={onDateChange(setTo)} inputProps={{ min: from || undefined }} />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={2.6}>
            <TextField
              label="Search order no. / customer / phone"
              size="small"
              fullWidth
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{ startAdornment: (<InputAdornment position="start"><MdSearch size={18} /></InputAdornment>) }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={1.6}>
            <TextField select label="Order Status" size="small" fullWidth value={status} onChange={(e) => { setStatus(e.target.value); setStatusGroup(''); setPage(0); }}>
              <MenuItem value="">All statuses</MenuItem>
              {Object.entries(STATUS_STYLES).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={1.4}>
            <TextField select label="Payment Status" size="small" fullWidth value={paymentStatus} onChange={(e) => { setPaymentStatus(e.target.value); setPage(0); }}>
              <MenuItem value="">All</MenuItem>
              {Object.keys(PAYMENT_STATUS_STYLES).map((k) => (
                <MenuItem key={k} value={k} sx={{ textTransform: 'capitalize' }}>{k}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={1.4}>
            <TextField select label="Payment Method" size="small" fullWidth value={paymentMode} onChange={(e) => { setPaymentMode(e.target.value); setPage(0); }}>
              <MenuItem value="">All</MenuItem>
              {PAYMENT_MODES.map((m) => (
                <MenuItem key={m} value={m}>{paymentModeLabel(m)}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={1.6}>
            <TextField select label="Sort by" size="small" fullWidth value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(0); }}>
              {SORT_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>
            Orders
            {(status || statusGroup) && (
              <Chip
                size="small"
                label={status ? STATUS_STYLES[status]?.label : cards.find((c) => c.group === statusGroup)?.label}
                onDelete={() => { setStatus(''); setStatusGroup(''); setPage(0); }}
                sx={{ ml: 1, fontWeight: 700 }}
              />
            )}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>{total.toLocaleString('en-IN')} orders</Typography>
        </Box>
        {(loading || exporting) && <LinearProgress />}
        <TableContainer sx={{ maxHeight: 580 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {COLUMNS.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.center ? 'center' : c.numeric ? 'right' : 'left'}
                    sx={{ fontWeight: 800, fontFamily: FONT, background: '#0f172a', color: '#fff', whiteSpace: 'nowrap', py: 1.5 }}
                  >
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} sx={{ textAlign: 'center', color: '#94a3b8', py: 6, fontFamily: FONT }}>
                    No orders match the selected filters.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r, idx) => (
                <TableRow
                  key={r.orderNumber || idx}
                  hover
                  sx={{
                    '&:nth-of-type(even)': { background: '#f8fafc' },
                    boxShadow: r.isDelayed ? 'inset 4px 0 0 #dc2626' : 'none',
                  }}
                >
                  {COLUMNS.map((c) => (
                    <TableCell
                      key={c.key}
                      align={c.center ? 'center' : c.numeric ? 'right' : 'left'}
                      sx={{
                        fontFamily: FONT,
                        whiteSpace: 'nowrap',
                        fontSize: 11.5,
                        fontWeight: c.key === 'orderNumber' || c.key === 'totalAmount' ? 700 : 500,
                        color: '#334155',
                      }}
                    >
                      {renderCell(c, r)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={Math.min(page, Math.max(Math.ceil(total / rowsPerPage) - 1, 0))}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid #e2e8f0' }}
        />
      </Paper>

      <Dialog open={notesOpen} onClose={() => setNotesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, fontFamily: FONT }}>Note</DialogTitle>
        <DialogContent dividers>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {(data?.notes || []).map((n, i) => (
              <Typography component="li" key={i} variant="body2" sx={{ mb: 1, fontFamily: FONT }}>{n}</Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderReport;
